import { Card, CardBody } from '@heroui/react';
import { ReactNode } from 'react';
import SmallLoadingSpinner from './SmallLoadingSpinner';

interface ReusableCardProps {
  title?: string;
  icon?: ReactNode;
  value?: string | number;
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  isHoverEffect?: boolean;
  isLoading?: boolean;
  isActive?: boolean;
  className?: string;
  filterId?: string;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  valueClassName?: string;
  titleClassName?: string;
  iconClassName?: string;
  iconBgClassName?: string;
  orientation?: 'vertical' | 'horizontal';
  variant?: 'default' | 'filter';
}

const StatsCard = ({
  title,
  icon,
  value,
  shadow,
  isHoverEffect,
  isLoading,
  isActive = false,
  className = '',
  filterId,
  onClick,
  size = 'md',
  valueClassName = '',
  titleClassName = '',
  iconClassName = '',
  iconBgClassName = '',
  orientation = 'horizontal',
  variant = 'default',
}: ReusableCardProps) => {
  const sizeConfig = {
    sm: {
      cardBody: 'py-1.5 px-2',
      gap: 'gap-1.5',
      iconWrapper: 'w-5 h-5',
      iconSize: 'w-2.5 h-2.5',
      valueText: 'text-xs font-bold',
      titleText: 'text-[10px] text-gray-500 whitespace-nowrap',
    },
    md: {
      cardBody: 'py-2 px-3',
      gap: 'gap-2',
      iconWrapper: 'w-6 h-6',
      iconSize: 'w-3 h-3',
      valueText: 'text-sm font-bold',
      titleText: 'text-xs text-gray-500 whitespace-nowrap',
    },
    lg: {
      cardBody: 'py-3 px-4',
      gap: 'gap-2.5',
      iconWrapper: 'w-8 h-8',
      iconSize: 'w-4 h-4',
      valueText: 'text-base font-bold',
      titleText: 'text-sm text-gray-500 whitespace-nowrap',
    },
  };

  const filterSizeConfig = {
    sm: {
      cardBody: 'py-2 px-1.5',
      gap: 'gap-2',
      iconWrapper: 'w-7 h-7',
      iconSize: 'w-3.5 h-3.5',
      valueText: 'text-xs font-bold tracking-tight',
      titleText: 'text-[9px] text-gray-500 font-medium whitespace-nowrap',
    },
    md: {
      cardBody: 'py-2.5 px-3',
      gap: 'gap-2.5',
      iconWrapper: 'w-8 h-8',
      iconSize: 'w-4 h-4',
      valueText: 'text-sm font-bold tracking-tight',
      titleText: 'text-xs text-gray-600 font-medium whitespace-nowrap',
    },
    lg: {
      cardBody: 'py-4 px-4',
      gap: 'gap-3',
      iconWrapper: 'w-10 h-10',
      iconSize: 'w-5 h-5',
      valueText: 'text-base font-bold tracking-tight',
      titleText: 'text-sm text-gray-600 font-medium whitespace-nowrap',
    },
  };

  const config =
    variant === 'filter' ? filterSizeConfig[size] : sizeConfig[size];

  const baseStyles = `
    border-1 rounded-lg transition-all duration-200
    ${onClick ? 'cursor-pointer' : ''}
  `;

  const filterStyles = `
    ${baseStyles}
    border-gray-200 bg-white/80 backdrop-blur-sm
    hover:border-gray-300 hover:shadow-sm
    active:bg-gray-50
    ${
      isActive
        ? 'border-primary bg-gradient-to-br from-primary/8 to-primary/4 shadow-md border-primary/30'
        : 'hover:shadow-md hover:border-primary/20'
    }
  `;

  const defaultStyles = `
    ${baseStyles}
    border-secondary/70 border-b-3
    ${shadow !== 'none' ? `shadow-${shadow}` : ''}
    ${
      isHoverEffect
        ? 'relative flex items-center justify-center overflow-hidden before:absolute before:h-0 before:w-0 before:rounded-full before:bg-secondary/10 before:duration-300 before:ease-out hover:scale-105 hover:shadow-md hover:before:h-56 hover:before:w-56'
        : ''
    }
    ${isActive ? 'border-secondary bg-secondary/10 border-b-4 shadow-md' : ''}
  `;

  const finalStyles = variant === 'filter' ? filterStyles : defaultStyles;

  return (
    <Card
      isPressable={!!onClick}
      shadow="none"
      onPress={onClick}
      data-testid={filterId ? `stats-card-${filterId}` : undefined}
      className={`${finalStyles} ${className}`}
    >
      <CardBody
        className={`flex justify-center items-center ${config.cardBody}`}
      >
        <div
          className={`flex ${
            orientation === 'horizontal'
              ? `flex-row items-center ${config.gap}`
              : `flex-col items-center justify-center ${config.gap}`
          }`}
        >
          {icon && (
            <div
              className={`flex items-center justify-center rounded-full shrink-0 transition-all ${config.iconWrapper} ${
                isActive && variant === 'filter'
                  ? 'bg-gradient-to-br from-primary to-primary/80 text-white shadow-md scale-110'
                  : isActive
                    ? iconBgClassName
                    : `${iconBgClassName} group-hover:scale-105`
              }`}
            >
              <div className={`${config.iconSize} ${iconClassName}`}>
                {icon}
              </div>
            </div>
          )}
          <div
            className={`flex items-center ${orientation === 'horizontal' ? 'gap-1.5' : 'flex-col'}`}
          >
            <p
              className={`${config.valueText} ${valueClassName} ${
                isActive && variant === 'filter' ? 'text-primary' : ''
              }`}
            >
              {isLoading ? <SmallLoadingSpinner /> : value}
            </p>
            <p className={`${config.titleText} ${titleClassName}`}>{title}</p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default StatsCard;
